import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './EmployeeList.css';

const EmployeeList = ({ employees, onEdit, onDelete, onViewDocuments, onRequestLeave, onCreateUserAccess, onEditPermissions, onEditDefaultBonuses }) => {
  const { hasModulePermission, hasPermission } = useAuth();
  const canUpdate = hasModulePermission('EMPLOYEES', 'update');
  const canEditPayrollBonuses = hasModulePermission('EMPLOYEES', 'update') || hasModulePermission('PAYROLL', 'update');
  const canDelete = hasModulePermission('EMPLOYEES', 'delete');
  const canViewDocuments = hasModulePermission('EMPLOYEES', 'view'); // Documents sont liés au module EMPLOYEES
  const canCreateLeave = hasModulePermission('LEAVES', 'create');
  const canManageUserPermissions = hasModulePermission('EMPLOYEES', 'update'); // Gestion des permissions utilisateur fait partie de la gestion des employés
  const [openMenuId, setOpenMenuId] = useState(null);
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const getStatusBadge = (statutActif) => {
    return statutActif ? (
      <span className="badge badge-success">Actif</span>
    ) : (
      <span className="badge badge-inactive">Inactif</span>
    );
  };

  const getContractTypeBadge = (typeContrat) => {
    const colors = {
      CDI: 'badge-cdi',
      CDD: 'badge-cdd',
      STAGE: 'badge-stage',
      FREELANCE: 'badge-freelance',
      SIVP: 'badge-sivp',
      KARAMA: 'badge-karama',
      GERANT: 'badge-gerant',
    };
    return <span className={`badge ${colors[typeContrat] || ''}`}>{typeContrat}</span>;
  };

  const handleMenuToggle = (employeeId) => {
    setOpenMenuId(openMenuId === employeeId ? null : employeeId);
  };

  const handleMenuAction = (action, employee) => {
    setOpenMenuId(null);
    if (typeof action === 'function') {
      action(employee);
    }
  };

  // Fermer le menu si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('.action-menu-container')) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openMenuId]);

  if (employees.length === 0) {
    return (
      <div className="empty-state">
        <p>Aucun employé trouvé.</p>
      </div>
    );
  }

  return (
    <div className="employee-list">
      <div className="table-container">
        <table className="employee-table">
          <thead>
            <tr>
              <th>Matricule</th>
              <th>Nom</th>
              <th>Prénom</th>
              <th>CIN</th>
              <th>CNSS</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Adresse</th>
              <th>Situation familiale</th>
              <th>Enfants</th>
              <th>Qualification</th>
              <th>Catégorie</th>
              <th>Échelon</th>
              <th>Date d'embauche</th>
              <th>Type de contrat</th>
              <th>Statut</th>
              <th className="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((employee) => (
              <tr key={employee.id}>
                <td>{employee.matricule || '-'}</td>
                <td>{employee.nom}</td>
                <td>{employee.prenom}</td>
                <td>{employee.cin || '-'}</td>
                <td>{employee.cnssNumber || '-'}</td>
                <td>{employee.email}</td>
                <td>{employee.telephone}</td>
                <td title={employee.adresse || ''}>{employee.adresse || '-'}</td>
                <td>{employee.maritalStatus === 'MARRIED' ? 'Marié(e)' : 'Célibataire'}</td>
                <td>{employee.children ?? 0}</td>
                <td>{employee.qualification}</td>
                <td>{employee.categorie || '-'}</td>
                <td>{employee.echellon || '-'}</td>
                <td>{formatDate(employee.dateEmbauche)}</td>
                <td>{getContractTypeBadge(employee.typeContrat)}</td>
                <td>{getStatusBadge(employee.statutActif)}</td>
                <td className="actions-column">
                  <div className="action-menu-container">
                    <button
                      className="action-menu-trigger"
                      onClick={() => handleMenuToggle(employee.id)}
                      title="Actions"
                      aria-label="Menu d'actions"
                    >
                      ⋮
                    </button>
                    {openMenuId === employee.id && (
                      <>
                        <div 
                          className="action-menu-overlay"
                          onClick={() => setOpenMenuId(null)}
                        />
                        <div className="action-menu-dropdown">
                          {canViewDocuments && (
                            <button
                              onClick={() => handleMenuAction(onViewDocuments, employee)}
                              className="action-menu-item"
                            >
                              <span className="action-icon">📁</span>
                              <span>Voir les documents</span>
                            </button>
                          )}
                          {canCreateLeave && (
                            <button
                              onClick={() => handleMenuAction(onRequestLeave, employee)}
                              className="action-menu-item"
                            >
                              <span className="action-icon">🏖️</span>
                              <span>Demander un congé</span>
                            </button>
                          )}
                          {canUpdate && onCreateUserAccess && !employee.userId && (
                            <button
                              onClick={() => handleMenuAction(onCreateUserAccess, employee)}
                              className="action-menu-item"
                            >
                              <span className="action-icon">🔑</span>
                              <span>Créer un accès utilisateur</span>
                            </button>
                          )}
                          {employee.userId && (
                            <>
                              <div className="action-menu-item action-menu-info">
                                <span className="action-icon">✓</span>
                                <span>Accès créé ({employee.userUsername || 'N/A'})</span>
                              </div>
                              {canManageUserPermissions && onEditPermissions && (
                                <button
                                  onClick={() => handleMenuAction(onEditPermissions, employee)}
                                  className="action-menu-item"
                                >
                                  <span className="action-icon">⚙️</span>
                                  <span>Permissions modules</span>
                                </button>
                              )}
                            </>
                          )}
                          {canEditPayrollBonuses && onEditDefaultBonuses && (
                            <button
                              onClick={() => handleMenuAction(onEditDefaultBonuses, employee)}
                              className="action-menu-item"
                            >
                              <span className="action-icon">💰</span>
                              <span>Éditer prime</span>
                            </button>
                          )}
                          {canUpdate && (
                            <button
                              onClick={() => handleMenuAction(onEdit, employee)}
                              className="action-menu-item"
                            >
                              <span className="action-icon">✏️</span>
                              <span>Modifier</span>
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleMenuAction(() => onDelete(employee.id), employee)}
                              className="action-menu-item action-menu-item-danger"
                            >
                              <span className="action-icon">🗑️</span>
                              <span>Supprimer</span>
                            </button>
                          )}
                          {!canUpdate && !canDelete && !canViewDocuments && !canCreateLeave && (
                            <div className="action-menu-item action-menu-info">
                              <span>Lecture seule</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeeList;

